import React, { useState } from 'react';
import { AppBar, Toolbar, IconButton, Menu, MenuItem, Avatar, Box, ListItemIcon, ListItemText } from '@mui/material';
import { Person, NewReleases, Extension, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './Header.scss';
import { useAuth } from '../../hooks/AuthProvider';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useTheme } from '../../hooks/ThemeProvider';
import { DarkModeToggle } from '../DarkModeToggle/DarkModeToggle';
import { AuthAnalytics, NavigationAnalytics } from '../../lib/analytics';
import { GoogleJwtPayload } from 'src/types/interfaces';

/**
 * Header component for the application.
 * Renders an AppBar with a logo, user avatar, and a menu for authenticated users.
 */
const Header: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isLoading, userProfile, isAuthenticated, logOut } = useAuth();
  const navigate = useNavigate();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleIconClick = () => navigate("/");
  const handleClose = () => setAnchorEl(null);

  const handleMenuClick = (path: string) => {
    handleClose();
    NavigationAnalytics.headerMenu(path);
    navigate(path);
  };

  const getAvatarImage = () => {
    // First try to get avatar from userProfile (backend)
    if (userProfile?.person?.avatarUrl) {
      return userProfile.person.avatarUrl;
    }

    // Fallback to JWT token picture
    try {
      const token = Cookies.get("social_at");
      if (token) {
        const decoded = jwtDecode<GoogleJwtPayload>(token);
        return decoded.picture || '/default-avatar.png';
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }

    return '/default-avatar.png';
  }

  useTheme();

  return (
    isAuthenticated && !isLoading ? <div className="header-container fade-in">
      <AppBar position="sticky" className="header glassmorphic-header">
        <Toolbar>
          <IconButton
            edge="start"
            className="header-title"
            color="inherit"
            aria-label="menu"
            onClick={handleIconClick}
          >
            <Avatar
              src={'/new-mindara-logo.jpg'}
              alt="Mindara logo"
              className="logo-avatar"
            />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DarkModeToggle size="medium" />

            <IconButton
              edge="end"
              color="inherit"
              aria-label="account menu"
              onClick={handleMenu}
              className="avatar-button"
            >
              <Avatar
                className='user-avatar'
                src={getAvatarImage()}
                alt="User profile"
              />
            </IconButton>
          </Box>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 72, horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            className="glassmorphic-menu"
          >
            <MenuItem onClick={() => handleMenuClick('/profile')} className="menu-item">
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="My Profile" />
            </MenuItem>
            <MenuItem onClick={() => handleMenuClick('/whats-new')} className="menu-item">
              <ListItemIcon>
                <NewReleases fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="What's new?" />
            </MenuItem>
            <MenuItem onClick={() => handleMenuClick('/integrations')} className="menu-item">
              <ListItemIcon>
                <Extension fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Integrations" />
            </MenuItem>
            <MenuItem onClick={() => {
              handleClose();
              AuthAnalytics.logout();
              logOut()
            }} className="menu-item">
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </div> : <></>
  );
}

export default Header;
